import type { ConsultaTv, SlideTv } from "@/components/tv/PainelTv";
import { useIndicadores, useMapa, useSncr, type Periodo } from "./api";
import { useCidadesResolvidas } from "./cidades";
import { DestaqueTv } from "./DestaqueTv";
import { KpiCards } from "./KpiCards";
import { MapaCidades } from "./MapaCidades";
import { SncrCards } from "./SncrCards";
import { TopCidades } from "./TopCidades";

const SECAO = "Silpi Farma";

/** Slides do Silpi Farma para o PainelTv — usados no modo TV do dashboard e no da Home. */
export function useSlidesSilpiFarmaTv(periodo: Periodo): { slides: SlideTv[]; consultas: ConsultaTv[] } {
  const indicadores = useIndicadores(periodo, { autoAtualizar: true });
  const mapa = useMapa(periodo, { autoAtualizar: true });
  const sncr = useSncr({ autoAtualizar: true });
  const { cidades } = useCidadesResolvidas(mapa.data);

  const slides: SlideTv[] = [
    {
      id: "farma-indicadores",
      secao: SECAO,
      titulo: "Indicadores",
      conteudo: <KpiCards data={indicadores.data} isLoading={indicadores.isLoading} variante="tv" />,
    },
    {
      id: "farma-sncr",
      secao: SECAO,
      titulo: "SNCR",
      conteudo: <SncrCards data={sncr.data} isLoading={sncr.isLoading} variante="tv" />,
    },
    {
      id: "farma-mapa",
      secao: SECAO,
      titulo: "Mapa de dispensações",
      conteudo: (
        <div className="flex h-full gap-8">
          <div className="relative z-0 min-w-0 flex-1 overflow-hidden rounded-xl">
            <MapaCidades cidades={cidades} metrica="dispensacoes" variante="tv" rotulosFixos={5} />
          </div>
          <aside className="flex w-80 flex-col justify-center gap-8">
            <DestaqueTv rotulo="Cidades com dispensação" valor={cidades.filter((c) => c.dispensacoes > 0).length} />
            <DestaqueTv rotulo="Cidades com farmácia cadastrada" valor={cidades.filter((c) => c.farmacias > 0).length} />
            {!!mapa.data?.dispensacoesSemLocalizacao && (
              <DestaqueTv rotulo="Dispensações ainda sem município" valor={mapa.data.dispensacoesSemLocalizacao} discreto />
            )}
          </aside>
        </div>
      ),
    },
    {
      id: "farma-cidades",
      secao: SECAO,
      titulo: "Cidades em destaque",
      conteudo: (
        <div className="grid h-full grid-cols-2 gap-12">
          <div>
            <h2 className="mb-4 text-3xl font-medium text-teal-400">Mais dispensações</h2>
            <TopCidades cidades={cidades} metrica="dispensacoes" limite={8} variante="tv" />
          </div>
          <div>
            <h2 className="mb-4 text-3xl font-medium text-amber-400">Mais farmácias cadastradas</h2>
            <TopCidades cidades={cidades} metrica="farmacias" limite={8} variante="tv" />
          </div>
        </div>
      ),
    },
  ];

  return { slides, consultas: [indicadores, mapa, sncr] };
}
